import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Shield, Sparkles, Truck, Facebook, Instagram, Search, ShoppingBag, X } from "lucide-react";
import InstagramCarousel from "@/components/InstagramCarousel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Home = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchRef = useRef(null);
  const navigate = useNavigate();

  // Search products as user types
  useEffect(() => {
    const searchProducts = async () => {
      if (searchQuery.trim().length < 2) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }
      
      setSearchLoading(true);
      try {
        const res = await axios.get(`${API}/products?search=${encodeURIComponent(searchQuery)}&limit=8`);
        setSearchResults(res.data);
        setShowSearchResults(true);
      } catch (error) {
        console.error("Search error:", error);
      } finally {
        setSearchLoading(false);
      }
    };

    const debounce = setTimeout(searchProducts, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed database first
        await axios.post(`${API}/seed`).catch(() => {});
        
        const categoriesRes = await axios.get(`${API}/categories/all`);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="relative bg-black min-h-screen" data-testid="home-page">
      {/* Tiled wallpaper background */}
      <div 
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage: `url('/uploads/website/msuk-wallpaper.png')`,
          backgroundRepeat: 'repeat',
          backgroundSize: '320px',
          backgroundPosition: 'top left'
        }}
      />
      
      {/* Main Layout - Centered Content */}
      <div className="relative z-10 bg-black">
        {/* MSUK Logo Section - Above Welcome Text */}
        <section className="w-full pt-8 pb-4 md:pt-12 md:pb-6 flex justify-center" style={{ backgroundColor: '#000000' }} data-testid="msuk-logo-section">
          <img 
            src="/uploads/website/msuk-logo-white.png"
            alt="Ministripes UK Logo"
            className="h-auto object-contain"
            style={{ 
              maxWidth: '360px',
              width: '70%',
              minWidth: '220px',
              filter: 'brightness(1.2)'
            }}
            data-testid="msuk-logo"
          />
        </section>

        {/* Business Info Banner - Above Car */}
        <div className="bg-black py-6 md:py-8 px-4" data-testid="homepage-announcement">
          <div className="max-w-4xl mx-auto text-center space-y-3">
            <p 
              className="text-lg md:text-xl lg:text-2xl text-white font-light tracking-wide"
              style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' }}
            >
              Graphics Specialists for Mini Cars
            </p>
            <p 
              className="text-base md:text-lg lg:text-xl text-white font-light tracking-wide"
              style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' }}
            >
              Midlands UK Based • Serving Customers Worldwide
            </p>
            <p 
              className="text-base md:text-lg lg:text-xl text-white font-light tracking-wide"
              style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' }}
            >
              Premium Grade Graphics, Decals & Stripes
            </p>
            <p 
              className="text-base md:text-lg lg:text-xl text-white font-light tracking-wide"
              style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' }}
            >
              Supply & Fit for Main Dealers, Trade & Enthusiasts
            </p>
          </div>
        </div>

        {/* Car Banner Section - Full Width Edge to Edge */}
        <section className="relative w-full overflow-hidden pt-8" style={{ backgroundColor: '#000000' }} data-testid="homepage-banner">
          {/* Red Dotted Wave Pattern Background */}
          <div 
            className="absolute inset-0 z-0 flex items-center justify-center"
            style={{ 
              pointerEvents: 'none'
            }}
          >
            <img 
              src="/uploads/website/red-wave-pattern.png"
              alt=""
              className="w-full h-full object-contain"
              style={{ 
                maxWidth: '100%',
                transform: 'scale(1.2)',
                filter: 'saturate(1.8) brightness(1.3) contrast(1.2)'
              }}
            />
          </div>
          <div className="relative z-10 w-full flex justify-center items-center" style={{ backgroundColor: 'transparent' }}>
            <div className="relative flex items-center justify-center" style={{ maxWidth: '100%' }}>
              <img 
                src="/uploads/mini-gp-transparent.png"
                alt="Mini Stripes UK"
                className="h-auto object-contain"
                style={{ 
                  minHeight: '250px', 
                  maxHeight: '400px', 
                  width: 'auto', 
                  maxWidth: '60vw'
                }}
              />
            </div>
          </div>
        </section>

        {/* Main Content */}
        <main className="flex-1">
          {/* Tagline Banner Section - Full Width with Background */}
          <section className="relative w-full py-10 md:py-16" style={{ backgroundColor: '#000000' }} data-testid="tagline-banner">
            {/* Pattern Background */}
            <div 
              className="absolute inset-0"
              style={{
                backgroundImage: `url('https://customer-assets.emergentagent.com/job_337353c4-cab4-46ce-9f12-02cfa3d402e5/artifacts/u3ohjz3m_pattern.png')`,
                backgroundSize: '100% 100%',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                opacity: 0.10
              }}
            />
            {/* Top fade overlay - fades pattern to black at top */}
            <div 
              className="absolute top-0 left-0 right-0 h-72 md:h-96 z-[1]"
              style={{
                background: 'linear-gradient(to bottom, #000000 0%, #000000 50%, transparent 100%)'
              }}
            />
            <div className="relative z-10 w-full px-4 sm:px-6 md:px-8 lg:px-12 flex flex-col items-center justify-center">
              {/* Centered Content */}
              <div className="flex flex-col items-center justify-center text-center">
                
                {/* Tagline Text - Centered */}
                <div className="text-center space-y-2 md:space-y-3">
                  {/* Mobile layout - 3 lines centered with nowrap */}
                  <div className="md:hidden leading-snug">
                    <p 
                      className="text-[11px] text-white font-light tracking-wide whitespace-nowrap"
                      style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' }}
                    >
                      From our Midlands workshop
                    </p>
                    <p 
                      className="text-[11px] text-white font-light tracking-wide whitespace-nowrap"
                      style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' }}
                    >
                      the historic home of Mini heritage we design
                    </p>
                    <p 
                      className="text-[11px] text-white font-light tracking-wide whitespace-nowrap"
                      style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' }}
                    >
                      and create products made for Mini lovers
                    </p>
                  </div>
                  {/* Desktop layout - 2 lines */}
                  <div className="hidden md:block space-y-2">
                    <p 
                      className="text-base lg:text-lg xl:text-xl 2xl:text-2xl text-white font-light tracking-wide"
                      style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' }}
                    >
                      From our Midlands workshop the historic home of Mini heritage
                    </p>
                    <p 
                      className="text-base lg:text-lg xl:text-xl 2xl:text-2xl text-white font-light tracking-wide"
                      style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' }}
                    >
                      we design and create products made for Mini lovers
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Social Media Icons */}
              <div className="flex items-center gap-4 mt-8">
                <a 
                  href="https://www.facebook.com/profile.php?id=61572637403452" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-12 h-12 bg-white rounded-full text-black hover:bg-black hover:text-white transition-all"
                  data-testid="tagline-facebook-link"
                >
                  <Facebook className="h-6 w-6" />
                </a>
                <a 
                  href="https://www.instagram.com/MinistripesUK" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-12 h-12 bg-white rounded-full text-black hover:bg-black hover:text-white transition-all"
                  data-testid="tagline-instagram-link"
                >
                  <Instagram className="h-6 w-6" />
                </a>
              </div>

              {/* Search Bar with Shop Button */}
              <div className="mt-8 w-full max-w-lg" ref={searchRef}>
                <div className="relative flex items-center gap-3">
                  {/* Search Input */}
                  <div className="relative flex-1">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={() => searchQuery.length >= 2 && setShowSearchResults(true)}
                      placeholder="Search for"
                      className="w-full pl-5 pr-14 py-4 border border-gray-300 rounded-full bg-white text-black text-base focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      data-testid="home-search-input"
                    />
                    {searchQuery ? (
                      <button
                        onClick={() => {
                          setSearchQuery('');
                          setShowSearchResults(false);
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 flex items-center justify-center rounded-full bg-black text-white hover:bg-gray-800"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    ) : (
                      <div className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 flex items-center justify-center rounded-full bg-black">
                        <Search className="h-5 w-5 text-white" />
                      </div>
                    )}
                  </div>
                  
                  {/* Shop Button */}
                  <Link
                    to="/products"
                    className="flex items-center justify-center h-14 w-14 bg-white rounded-full text-black hover:bg-black hover:text-white transition-all shrink-0"
                    data-testid="home-shop-button"
                  >
                    <ShoppingBag className="h-6 w-6" />
                  </Link>
                </div>

                {/* Live Search Results Dropdown */}
                {showSearchResults && searchResults.length > 0 && (
                  <div className="absolute mt-2 w-full max-w-md bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-50">
                    <div className="max-h-80 overflow-y-auto">
                      {searchResults.map((product) => (
                        <Link
                          key={product.id}
                          to={`/product/${product.id}`}
                          onClick={() => {
                            setShowSearchResults(false);
                            setSearchQuery('');
                          }}
                          className="flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                        >
                          <div className="w-14 h-14 bg-gray-100 rounded-lg overflow-hidden shrink-0">
                            <img 
                              src={product.image_url} 
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
                            <p className="text-sm text-orange-600 font-semibold">£{product.price?.toFixed(2)}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                    <Link
                      to={`/products?search=${encodeURIComponent(searchQuery)}`}
                      onClick={() => setShowSearchResults(false)}
                      className="block p-3 text-center text-sm font-medium text-orange-600 hover:bg-orange-50 border-t border-gray-200"
                    >
                      View all results →
                    </Link>
                  </div>
                )}

                {/* Loading indicator */}
                {searchLoading && searchQuery.length >= 2 && (
                  <div className="absolute mt-2 w-full max-w-md bg-white rounded-xl shadow-lg border border-gray-200 p-4 text-center z-50">
                    <p className="text-sm text-gray-500">Searching...</p>
                  </div>
                )}

                {/* No results message */}
                {showSearchResults && searchQuery.length >= 2 && searchResults.length === 0 && !searchLoading && (
                  <div className="absolute mt-2 w-full max-w-md bg-white rounded-xl shadow-lg border border-gray-200 p-4 text-center z-50">
                    <p className="text-sm text-gray-500">No products found for "{searchQuery}"</p>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Video Section */}
          <section className="relative w-full overflow-hidden bg-black min-h-[450px] md:min-h-[700px] lg:min-h-[800px]">
            {/* Video Background */}
            <video
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              className="absolute inset-0 w-full h-full object-cover z-0"
              style={{ filter: 'brightness(0.8)', objectPosition: 'center top' }}
            >
              <source src="https://customer-assets.emergentagent.com/job_337353c4-cab4-46ce-9f12-02cfa3d402e5/artifacts/bs2tafax_25133.mp4" type="video/mp4" />
            </video>
            
            {/* Dark Tint Overlay */}
            <div className="absolute inset-0 bg-black/40 z-[1]"></div>
            
            {/* Top crop overlay */}
            <div className="absolute top-0 left-0 right-0 h-16 md:h-24 bg-gradient-to-b from-black to-transparent z-[1]"></div>
            
            {/* Bottom crop overlay */}
            <div className="absolute bottom-0 left-0 right-0 h-16 md:h-24 bg-gradient-to-t from-black to-transparent z-[1]"></div>
            
          </section>

          {/* Trust Badges & Brand Logos - Centered between Video and Carousel */}
          <section className="relative w-full bg-black py-20 md:py-28 pb-12 md:pb-16 flex flex-col items-center justify-center" data-testid="trust-badges-section">
            {/* Pattern Background */}
            <div 
              className="absolute inset-0"
              style={{
                backgroundImage: `url('https://customer-assets.emergentagent.com/job_337353c4-cab4-46ce-9f12-02cfa3d402e5/artifacts/u3ohjz3m_pattern.png')`,
                backgroundSize: '100% 100%',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                opacity: 0.10
              }}
            />
            {/* Trust Badges */}
            <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8 mb-16 md:mb-20">
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-3 sm:gap-10 md:gap-14">
                <div className="flex flex-col items-center text-center">
                  <div className="flex items-center justify-center w-14 h-14 md:w-18 md:h-18 bg-white rounded-full mb-5">
                    <Shield className="h-7 w-7 md:h-9 md:w-9 text-black" />
                  </div>
                  <h3 
                    className="text-base md:text-lg font-light text-white mb-2 tracking-wide"
                    style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}
                  >
                    Premium Brand Vinyl's
                  </h3>
                  <p 
                    className="text-sm md:text-base text-gray-300 leading-relaxed font-light tracking-wide"
                    style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}
                  >
                    Polymeric & Cast Vehicle Grade. Keeping your graphics looking sharp for years.
                  </p>
                </div>
                
                <div className="flex flex-col items-center text-center">
                  <div className="flex items-center justify-center w-14 h-14 md:w-18 md:h-18 bg-white rounded-full mb-5">
                    <Truck className="h-7 w-7 md:h-9 md:w-9 text-black" />
                  </div>
                  <h3 
                    className="text-base md:text-lg font-light text-white mb-2 tracking-wide"
                    style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}
                  >
                    Premium Shipping
                  </h3>
                  <p 
                    className="text-sm md:text-base text-gray-300 leading-relaxed font-light tracking-wide"
                    style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}
                  >
                    Orders professionally packed shipped via Tracked Royal Mail / UPS
                  </p>
                </div>
                
                <div className="flex flex-col items-center text-center">
                  <div className="flex items-center justify-center w-14 h-14 md:w-18 md:h-18 bg-white rounded-full mb-5">
                    <Sparkles className="h-7 w-7 md:h-9 md:w-9 text-black" />
                  </div>
                  <h3 
                    className="text-base md:text-lg font-light text-white mb-2 tracking-wide"
                    style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}
                  >
                    Easy Application
                  </h3>
                  <p 
                    className="text-sm md:text-base text-gray-300 leading-relaxed font-light tracking-wide"
                    style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}
                  >
                    Our air-release option vinyls make fitting your graphics quick and stress-free
                  </p>
                </div>
              </div>
            </div>
            
            {/* Brand Logos */}
            <div className="relative z-10 w-full max-w-7xl mx-auto px-2 sm:px-4 md:px-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_9a897fba-e8dc-4e39-998a-a577303a6f73/artifacts/of8rsh7b_3.png"
                alt="Brand Logos"
                className="w-full h-auto"
                style={{ objectFit: 'contain' }}
              />
            </div>
          </section>

          {/* Instagram Carousel Section - Full Width */}
          <section className="relative w-full py-0 md:py-0 -mt-1" style={{ backgroundColor: '#000000' }}>
            <div className="relative z-10 w-full px-2 sm:px-4 md:px-8 lg:px-12">
              <InstagramCarousel />
            </div>
          </section>

          {/* End of main content */}
        </main>
        </div>
      </div>
    );
  };

export default Home;